import { FormattedMessage, useIntl } from 'react-intl';

import {
  Row,
  Col,
  Form,
  Input,
  Checkbox,
  Radio,
  Typography,
  FormInstance,
  Button,
  Divider,
  Select,
} from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { CompaniesInfiniteSelectField } from '@/components/_common';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { InfiniteData, useQueryClient } from 'react-query';
import { PageResult } from '@/core/request/PageResult';
import { Group } from '@/core/domain/operational/Groups/Groups.types';
import {
  StockPartsFormValues,
  StockPartsOrderBy,
} from '@/core/domain/reports/stock/Report.types';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import {
  RegistrationsDefaultCode,
  RegistrationsDefaultCodeLabel,
} from '@/types/Register';

interface Props {
  form: FormInstance<StockPartsFormValues>;
}

function PartReportForm({ form }: Props) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const { data: matrix } = useMatrix();

  const defaultPartCode = matrix?.matrixSettings.defaultPartCode;

  return (
    <Form form={form}>
      <Row gutter={12}>
        <Col span={24}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'parts.modal.report.form.description',
            })}
            name="partDescription"
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'parts.modal.report.form.group',
            })}
            name="subGroupIds"
          >
            <GroupsInfiniteSelectField
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              dropdownRender={(menu) => (
                <>
                  <Button
                    block
                    type="text"
                    onClick={() => {
                      const [groupsDataQuery] = queryClient.getQueriesData<
                        InfiniteData<PageResult<Group>>
                      >(['groups']);

                      if (groupsDataQuery) {
                        const groups = flatInfiniteData(groupsDataQuery[1]);

                        form.setFields([
                          {
                            name: 'subGroupIds',
                            value: groups
                              .map((c) => c.subGroups.map((item) => item.id))
                              .flat(),
                            errors: [],
                          },
                        ]);
                      }
                    }}
                  >
                    Selecionar Tudo
                  </Button>

                  <Divider style={{ margin: '8px 0' }} />
                  {menu}
                </>
              )}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem name="manufacturerIds" label={'Fabricante'}>
            <ManufacturerInfiniteSelectField
              queryFilter={{ active: true }}
              mode={'multiple'}
              maxTagCount="responsive"
              allowClear
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem
            name="manufacturingCodeFrom"
            label="Cód. Fabricante inicial"
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem
            name="manufacturingCodeTo"
            label="Cód. Fabricante final"
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem name="companiesIds" label={'Empresa'}>
            <CompaniesInfiniteSelectField
              queryFilter={{ active: true }}
              mode={'multiple'}
              allowClear
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem name="active" label="Atividade">
            <Select
              options={[
                { label: 'Ativo', value: true },
                { label: 'Inativo', value: false },
              ]}
              allowClear
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem name="localizationFrom" label="Localização de">
            <Input />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem name="localizationTo" label="Até">
            <Input />
          </FloatingFormItem>
        </Col>

        <Col md={11} span={12}>
          <FloatingFormItem valuePropName="checked" name="showDetailProducts">
            <Checkbox>
              <FormattedMessage id="parts.modal.report.form.detailTheProducts" />
            </Checkbox>
          </FloatingFormItem>

          <FloatingFormItem valuePropName="checked" name="showPartApplication">
            <Checkbox>
              <FormattedMessage id="parts.modal.report.form.viewPartApplication" />
            </Checkbox>
          </FloatingFormItem>

          <FloatingFormItem valuePropName="checked" name="showRealCost">
            <Checkbox>
              <FormattedMessage id="parts.modal.report.form.viewActualCostPart" />
            </Checkbox>
          </FloatingFormItem>

          <FloatingFormItem valuePropName="checked" name="showPartCodesOnCSV">
            <Checkbox>
              <FormattedMessage id="parts.modal.report.form.showPartCodesOnCSV" />
            </Checkbox>
          </FloatingFormItem>

          <FloatingFormItem valuePropName="checked" name="showPartCfop">
            <Checkbox>
              <FormattedMessage id="parts.modal.report.form.showPartCfop" />
            </Checkbox>
          </FloatingFormItem>
        </Col>

        <Col md={11} sm={24}>
          <Typography.Title level={5}>
            <FormattedMessage id="parts.modal.report.form.inhibitPartsStockWithZero" />
          </Typography.Title>

          <Form.Item
            name="showPartsWithZeroStock"
            labelAlign="left"
            initialValue={false}
          >
            <Radio.Group>
              <Radio value={true}>
                <FormattedMessage id="parts.modal.report.form.yes" />
              </Radio>

              <Radio value={false}>
                <FormattedMessage id="parts.modal.report.form.no" />
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Typography.Title level={5}>
            <FormattedMessage id="parts.modal.report.form.order" />
          </Typography.Title>

          <Form.Item
            name="orderByColumn"
            labelAlign="left"
            initialValue={StockPartsOrderBy.description}
          >
            <Radio.Group>
              <Radio value={StockPartsOrderBy.description}>
                <FormattedMessage id="parts.modal.report.form.descriptionOrder" />
              </Radio>

              <Radio value={StockPartsOrderBy.Code}>
                {defaultPartCode
                  ? RegistrationsDefaultCodeLabel[defaultPartCode]
                  : RegistrationsDefaultCodeLabel[
                      RegistrationsDefaultCode.internalCode
                    ]}
              </Radio>

              <Radio value={StockPartsOrderBy.localization}>
                <FormattedMessage id="parts.modal.report.form.localization" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

export default PartReportForm;
